import React from "react";
import { Card, CtaBand, Section, SectionHead } from "./components/ui";
import { Icon } from "./components/Icon";
import { ContactButton } from "./components/ContactButton";
import { CATEGORIES, CUSTOMER_LOGOS, TESTIMONIALS } from "./content/site";
import Logos from "./components/Logos";
import { Testimonials } from "../common/layout/testimonials/Testimonials";

const DATA_FEEDS_LINK = "https://data.ch-aviation.com/";
const PARTNERS_LINK = "/data-store/partners";

const STEPS = [
	["1", "Browse the store"],
	["2", "Select the feeds"],
	["3", "Request a proposal"],
	["4", "Set up delivery"],
];

const TEASER = [
	[
		"Aircraft",
		"Aircraft",
		"Commercial aviation aircraft and business jets with codes, specs, and operators.",
	],
	[
		"Operators",
		"Operators",
		"Airlines and business jet operators worldwide with codes and profiles.",
	],
	[
		"Airports",
		"Airports",
		"Airport profiles, codes, and locations for the airports of the world.",
	],
	[
		"Flight activity",
		"Flights",
		"ADS-B-based flight activity with origin, destination, and times.",
	],
	[
		"Ownership & valuations",
		"Aircraft Base Values",
		"Current and future base values for every tracked aircraft type.",
	],
	[
		"News",
		"Aviation News",
		"80,000+ aviation articles, tagged and filterable. Great for AI tools.",
	],
];

const BUILDS = [
	[
		"Asset management tools",
		"Track aircraft, owners, lease terms, and renewal dates in one system.",
		"Aircraft · Ownership and valuations · Operators",
	],
	[
		"Fleet dashboards",
		"Show fleet composition, ages, and utilization across operators or portfolios.",
		"Aircraft · Operators · Flight activity",
	],
	[
		"Risk and pricing models",
		"Price insurance, financing, or aircraft asset risk.",
		"Ownership and valuations · Safety · Flight activity",
	],
	[
		"AI assistants and agents",
		"Build assistants, agents, and search tools that work with aviation data and content.",
		"News · plus any other feed",
	],
	[
		"Market intelligence and research",
		"Analyse the industry, study fleet movements, market dynamics, route economics.",
		"Any combination of feeds",
	],
	[
		"Operational tools",
		"Support MRO planning, route operations, capacity analysis, or contact management.",
		"Maintenance · Flight activity · Operators",
	],
];

const HOW = [
	[
		"1",
		"Browse the store",
		"Browse the full storefront without registering. Every feed has a description and schema. Request a sample with just your name and email.",
	],
	[
		"2",
		"Select the feeds",
		"Pick the feeds you need for what you're building. Not sure which fit? Talk to us at any point and we'll help you scope.",
	],
	[
		"3",
		"Request a proposal",
		"Tell us which feeds you want, how you'd like them delivered, and what you're building. We come back with a proposal.",
	],
	[
		"4",
		"Set up delivery",
		"Once we agree, your data flows automatically into your system via AWS S3, Snowflake, or another method. No manual work.",
	],
];

export default function HomePage() {
	return (
		<>
			{/* Hero */}
			<section className="section-hero container tw-bg-white">
				<span className="tw-eyebrow">ch-aviation Data Store</span>
				<h1 className="h2 tw-my-5 tw-max-w-6xl">
					The data feeds you need to build something that works
				</h1>
				<p className="tw-lead tw-mb-8 tw-max-w-4xl">
					The ch-aviation Data Store is where you get aviation data
					delivered straight to your systems. Dozens of ready-to-use
					feeds covering aircraft, airlines, airports, flights,
					valuations, accidents, and more. Pick the ones you need, and
					we deliver them to you, kept up to date, so you can put them
					to work in whatever you&apos;re building.
				</p>
				<div className="tw-flex tw-gap-3">
					<a
						href={DATA_FEEDS_LINK}
						className="btn btn--primary"
						target="_blank"
						rel="noopener noreferrer"
					>
						Browse data feeds &rarr;
					</a>
					<ContactButton className="btn btn--secondary">
						Contact us
					</ContactButton>
				</div>

				<div className="cards-list tw-flex tw-overflow-x-auto tw-md:overflow-hidden">
					{STEPS.map(([n, t]) => (
						<div
							key={n}
							className="card tw-flex-1 tw-border-r tw-border-line tw-px-5 tw-py-4 tw-last:border-r-0"
						>
							<div className=" tw-text-[13px] tw-font-bold tw-text-red">
								{n}
							</div>
							<div className=" tw-mt-0.5 tw-text-[15px] tw-font-semibold tw-text-ink">
								{t}
							</div>
						</div>
					))}
				</div>
				<div className=" tw-mt-3.5 tw-text-[14px] tw-italic tw-text-muted">
					Not sure where to start? Talk to us at any point.
				</div>
			</section>

			{/* Categories */}
			<Section variant="surface" className="section-categories">
				<SectionHead
					title="Eight categories of aviation data"
					intro="Every feed is organized into one of eight categories, so you can find what you need fast."
				/>
				<div className="cards-list tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2 lg:tw-grid-cols-4">
					{CATEGORIES.map((c) => (
						<Card
							key={c.name}
							title={c.name}
							body={c.body}
							pill={c.count}
							icon={<Icon name={c.icon} />}
						/>
					))}
				</div>
			</Section>

			{/* Unification */}
			<Section className="section-unification">
				<div className="tw-text-center">
					<SectionHead
						center
						title="Data that fits together out of the box."
						intro="ch-aviation has spent 20+ years building the reference data the aviation industry uses. Every aircraft, every operator, every airport has a unique ch-aviation identifier. Every feed uses these identifiers, ours and our partners'. So when you combine feeds, they join cleanly. No manual matching, no surprises."
					/>
					<div className="well tw-border tw-border-line tw-bg-surface p-6 tw-text-left">
						<p className="title tw-text-muted">Aircraft feed</p>
						<section className="tw-flex tw-flex-wrap tw-gap-4 tw-rounded tw-border tw-border-line tw-bg-white tw-text-[13px]">
							<span className="tw-rounded tw-bg-tint tw-px-2 tw-py-0.5 tw-font-semibold tw-text-red">
								Aircraft ID = 621
							</span>
							<span>Type = B787</span>
							<span>Registration = D-AIZA</span>
							<span>Operator = LH</span>
						</section>
						<div className="tw-py-2.5 tw-text-center tw-font-semibold tw-text-red">
							<span className="badge tw-rounded tw-border tw-border-dashed tw-border-red/40 tw-bg-tint tw-px-3 tw-py-1">
								joined on Aircraft ID
							</span>
						</div>
						<p className="title tw-text-muted">
							Flight activity feed
						</p>
						<section className="tw-flex tw-flex-wrap tw-gap-4 tw-rounded tw-border tw-border-line tw-bg-white tw-text-[13px]">
							<span className="tw-rounded tw-bg-tint tw-px-2 tw-py-0.5 tw-font-semibold tw-text-red">
								Aircraft ID = 621
							</span>
							<span>Callsign = DLH1234</span>
							<span>Origin = FRA</span>
							<span>Destination = JFK</span>
						</section>
					</div>
				</div>
			</Section>

			{/* Storefront teaser */}
			<Section variant="surface">
				<SectionHead
					title="A look inside the store."
					intro="A few of the 40+ feeds in the catalog. Browse the full storefront →"
				/>
				<div className="cards-list tw-grid tw-grid-cols-1 tw-gap-3.5 md:tw-grid-cols-3">
					{TEASER.map(([badge, name, body]) => (
						<div
							key={name}
							className="card typography tw-rounded-card tw-border tw-border-line tw-bg-white p-4"
						>
							<div className="tw-text-[11px] tw-font-semibold uppercase tracking-wide tw-text-muted">
								{badge}
							</div>
							<h3 className="h6">{name}</h3>
							<p>{body}</p>
							<a
								className="tw-mt-3 tw-text-[12.5px] tw-font-semibold tw-text-red"
								href="https://data.ch-aviation.com/"
								target="_blank"
								rel="noopener noreferrer"
							>
								View details &rarr;
							</a>
						</div>
					))}
				</div>
			</Section>

			{/* Who uses it */}
			<Section>
				<div className="tw-text-center">
					<SectionHead
						center
						title="Used by aviation companies of all kinds."
						intro="Used by anyone working with aviation data, including the companies that build products on top of it."
					/>

					<Logos logos={CUSTOMER_LOGOS} />
				</div>
			</Section>

			{/* For data providers */}
			<section className="tw-bg-tint">
				<div className="container tw-flex tw-flex-col tw-items-start tw-justify-between tw-gap-7 md:tw-flex-row md:tw-items-center">
					<div className="tw-max-w-3xl">
						<h2 className="tw-mb-2 tw-text-[26px]">
							For data providers.
						</h2>
						<p className="tw-text-[15px]">
							ch-aviation is always looking for new data
							providers. We standardize third-party data to a
							common format, add our identifiers, and build it
							into our data feeds. Buyers find more of what they
							need in one place, all built to work together. Every
							provider benefits.
						</p>
					</div>
					<a href={PARTNERS_LINK} className="btn btn--primary">
						Read about the partner programme &rarr;
					</a>
				</div>
			</section>

			{/* Things people build */}
			<Section>
				<SectionHead title="Things people build with ch-aviation data." />
				<div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-3">
					{BUILDS.map(([t, d, f]) => (
						<div key={t} className="card">
							<h3 className="tw-mb-1.5 tw-text-base">{t}</h3>
							<p className="tw-text-[13.5px] tw-leading-relaxed">
								{d}
							</p>
							<div className="tw-mt-3 tw-text-[11.5px] tw-text-muted">
								{f}
							</div>
						</div>
					))}
				</div>
			</Section>

			{/* How it works */}
			<Section variant="surface">
				<SectionHead title="How it works." />
				<div className="tw-grid tw-grid-cols-1 tw-gap-5 md:tw-grid-cols-4">
					{HOW.map(([n, t, d]) => (
						<div key={n}>
							<div className="tw-mb-3.5 tw-flex tw-h-12 tw-w-12 tw-items-center tw-justify-center tw-rounded-full tw-bg-red tw-text-[15px] tw-font-bold tw-text-white">
								{n}
							</div>
							<h3 className="h5 tw-mb-1.5">{t}</h3>
							<p className="tw-text-[13.5px] tw-leading-relaxed">
								{d}
							</p>
						</div>
					))}
				</div>
				<div className="tw-mt-3.5 tw-text-[14px] tw-italic tw-text-muted">
					Not sure where to start? Talk to us at any point.
				</div>
			</Section>

			<Testimonials
				testimonials={TESTIMONIALS}
				title="What our customers say"
			/>

			<CtaBand
				title="Have aviation data needs?"
				body="Tell us what you're building. We'll help you scope the right feeds."
				button="Talk to us"
			/>
		</>
	);
}
